<template>
  <div :class="themeClasses.pageContainer" class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div :class="themeClasses.card" class="card">
          <div :class="themeClasses.cardHeader" class="card-header">
            <h3 :class="themeClasses.cardTitle" class="card-title">
              <i class="bi bi-currency-dollar"></i>
              Reportes de Ingresos del Sistema
            </h3>
            <div class="card-tools">
              <button type="button" :class="themeClasses.buttonSuccess" class="btn btn-success btn-sm" @click="exportarReporte">
                <i class="bi bi-file-excel"></i>
                Exportar Excel
              </button>
            </div>
          </div>
          <div :class="themeClasses.cardBody" class="card-body">
            <!-- Filtros de período -->
            <div class="row mb-4">
              <div class="col-md-3">
                <label :class="themeClasses.label" class="form-label">Período</label>
                <select :class="themeClasses.formControl" class="form-select" v-model="filtros.periodo" @change="cargarDatos">
                  <option value="mes_actual">Mes Actual</option>
                  <option value="mes_anterior">Mes Anterior</option>
                  <option value="trimestre">Último Trimestre</option>
                  <option value="ano">Año Actual</option>
                  <option value="personalizado">Período Personalizado</option>
                </select>
              </div>
              <div class="col-md-3" v-if="filtros.periodo === 'personalizado'">
                <label class="form-label">Fecha Desde</label>
                <input type="date" class="form-control" v-model="filtros.fecha_desde" @change="cargarDatos">
              </div>
              <div class="col-md-3" v-if="filtros.periodo === 'personalizado'">
                <label class="form-label">Fecha Hasta</label>
                <input type="date" class="form-control" v-model="filtros.fecha_hasta" @change="cargarDatos">
              </div>
              <div class="col-md-3">
                <label class="form-label">Moneda</label>
                <select class="form-select" v-model="filtros.moneda" @change="cargarDatos">
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="all">Todas</option>
                </select>
              </div>
            </div>

            <!-- Métricas principales -->
            <div class="row mb-4">
              <div class="col-md-3">
                <div class="card text-center">
                  <div class="card-body">
                    <i class="bi bi-cash-stack fs-1 text-success"></i>
                    <h4>${{ metricas.ingresos_totales }}</h4>
                    <p class="mb-0">Ingresos Totales</p>
                    <small class="text-muted">
                      <span :class="metricas.variacion_ingresos >= 0 ? 'text-success' : 'text-danger'">
                        <i :class="metricas.variacion_ingresos >= 0 ? 'bi bi-arrow-up' : 'bi bi-arrow-down'"></i>
                        {{ Math.abs(metricas.variacion_ingresos) }}%
                      </span>
                      vs período anterior
                    </small>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card text-center">
                  <div class="card-body">
                    <i class="bi bi-credit-card fs-1 text-primary"></i>
                    <h4>{{ metricas.suscripciones_activas }}</h4>
                    <p class="mb-0">Suscripciones Activas</p>
                    <small class="text-muted">
                      <span :class="metricas.variacion_suscripciones >= 0 ? 'text-success' : 'text-danger'">
                        <i :class="metricas.variacion_suscripciones >= 0 ? 'bi bi-arrow-up' : 'bi bi-arrow-down'"></i>
                        {{ Math.abs(metricas.variacion_suscripciones) }}%
                      </span>
                      vs período anterior
                    </small>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card text-center">
                  <div class="card-body">
                    <i class="bi bi-graph-up fs-1 text-info"></i>
                    <h4>${{ metricas.ingreso_promedio_cliente }}</h4>
                    <p class="mb-0">ARPU</p>
                    <small class="text-muted">Ingreso promedio por usuario</small>
                  </div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="card text-center">
                  <div class="card-body">
                    <i class="bi bi-percent fs-1 text-warning"></i>
                    <h4>{{ metricas.tasa_retencion }}%</h4>
                    <p class="mb-0">Tasa de Retención</p>
                    <small class="text-muted">Últimos 12 meses</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Gráfico de ingresos por mes -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5>Evolución de Ingresos</h5>
                  </div>
                  <div class="card-body">
                    <canvas ref="ingresoChart" height="100"></canvas>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ingresos por plan -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5>Ingresos por Plan</h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead>
                          <tr>
                            <th>Plan</th>
                            <th>Suscripciones</th>
                            <th>Ingresos</th>
                            <th>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="plan in ingresosPorPlan" :key="plan.nombre">
                            <td>
                              <span class="badge" :class="getPlanBadgeClass(plan.nombre)">
                                {{ plan.nombre }}
                              </span>
                            </td>
                            <td>{{ plan.suscripciones }}</td>
                            <td>${{ plan.ingresos }}</td>
                            <td>{{ plan.porcentaje }}%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header">
                    <h5>Métodos de Pago</h5>
                  </div>
                  <div class="card-body">
                    <canvas ref="metodosChart" height="200"></canvas>
                  </div>
                </div>
              </div>
            </div>

            <!-- Métricas detalladas -->
            <div class="row">
              <div class="col-md-4">
                <div class="card">
                  <div class="card-header">
                    <h5>Nuevas Suscripciones</h5>
                  </div>
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Este mes:</span>
                      <strong>{{ metricas.nuevas_suscripciones_mes }}</strong>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Mes anterior:</span>
                      <span>{{ metricas.nuevas_suscripciones_mes_anterior }}</span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                      <span>Variación:</span>
                      <span :class="metricas.variacion_nuevas >= 0 ? 'text-success' : 'text-danger'">
                        <i :class="metricas.variacion_nuevas >= 0 ? 'bi bi-arrow-up' : 'bi bi-arrow-down'"></i>
                        {{ Math.abs(metricas.variacion_nuevas) }}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card">
                  <div class="card-header">
                    <h5>Cancelaciones</h5>
                  </div>
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Este mes:</span>
                      <strong class="text-danger">{{ metricas.cancelaciones_mes }}</strong>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Mes anterior:</span>
                      <span>{{ metricas.cancelaciones_mes_anterior }}</span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                      <span>Tasa cancelación:</span>
                      <span class="text-warning">{{ metricas.tasa_cancelacion }}%</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card">
                  <div class="card-header">
                    <h5>Ingresos Recurrentes</h5>
                  </div>
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>MRR:</span>
                      <strong class="text-success">${{ metricas.mrr }}</strong>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>ARR:</span>
                      <strong class="text-info">${{ metricas.arr }}</strong>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                      <span>LTV promedio:</span>
                      <span>${{ metricas.ltv_promedio }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue'
import axios from 'axios'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

import themeMixin from '@/mixins/themeMixin'

export default {
  mixins: [themeMixin],
  name: 'ReportesIngresos',
  setup() {
    const metricas = ref({})
    const ingresosPorPlan = ref([])
    const ingresoChart = ref(null)
    const metodosChart = ref(null)
    let chartInstances = []
    
    const filtros = ref({
      periodo: 'mes_actual',
      fecha_desde: '',
      fecha_hasta: '',
      moneda: 'USD'
    })

    const cargarDatos = async () => {
      try {
        const params = { ...filtros.value }
        const [metricasRes, planesRes] = await Promise.all([
          axios.get('/api/admin/reportes/ingresos/metricas', { params }),
          axios.get('/api/admin/reportes/ingresos/por-plan', { params })
        ])
        
        metricas.value = metricasRes.data
        ingresosPorPlan.value = planesRes.data
        
        await nextTick()
        crearGraficos()
      } catch (error) {
        console.error('Error loading datos:', error)
      }
    }

    const crearGraficos = () => {
      // Limpiar gráficos existentes
      chartInstances.forEach(chart => chart.destroy())
      chartInstances = []

      // Gráfico de evolución de ingresos
      if (ingresoChart.value && metricas.value.evolucion_ingresos) {
        const ctx = ingresoChart.value.getContext('2d')
        const chart = new ChartJS(ctx, {
          type: 'line',
          data: {
            labels: metricas.value.evolucion_ingresos.map(item => item.mes),
            datasets: [{
              label: 'Ingresos ($)',
              data: metricas.value.evolucion_ingresos.map(item => item.ingresos),
              borderColor: 'rgb(75, 192, 192)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              tension: 0.1
            }]
          },
          options: {
            responsive: true,
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    return '$' + value.toLocaleString()
                  }
                }
              }
            }
          }
        })
        chartInstances.push(chart)
      }

      // Gráfico de métodos de pago
      if (metodosChart.value && metricas.value.metodos_pago) {
        const ctx = metodosChart.value.getContext('2d')
        const chart = new ChartJS(ctx, {
          type: 'doughnut',
          data: {
            labels: metricas.value.metodos_pago.map(item => item.metodo),
            datasets: [{
              data: metricas.value.metodos_pago.map(item => item.porcentaje),
              backgroundColor: [
                '#FF6384',
                '#36A2EB',
                '#FFCE56',
                '#4BC0C0',
                '#9966FF'
              ]
            }]
          },
          options: {
            responsive: true,
            plugins: {
              legend: {
                position: 'bottom'
              }
            }
          }
        })
        chartInstances.push(chart)
      }
    }

    const exportarReporte = async () => {
      try {
        const response = await axios.get('/api/admin/reportes/ingresos/exportar', {
          params: filtros.value,
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `reporte_ingresos_${new Date().toISOString().split('T')[0]}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        console.error('Error exportando reporte:', error)
      }
    }

    const getPlanBadgeClass = (plan) => {
      const classes = {
        basico: 'bg-secondary',
        profesional: 'bg-info',
        empresarial: 'bg-warning',
        premium: 'bg-success'
      }
      return classes[plan?.toLowerCase()] || 'bg-primary'
    }

    onMounted(() => {
      cargarDatos()
    })

    return {
      metricas,
      ingresosPorPlan,
      filtros,
      ingresoChart,
      metodosChart,
      cargarDatos,
      exportarReporte,
      getPlanBadgeClass
    }
  }
}
</script>

<style>
.card {
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
}

canvas {
  max-height: 400px;
}
</style>
